import { css, html, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BaseComponent } from '../../../base-component/base-component';
import { StoreState } from '../../../../store/store.interface';
import { shouldClearOffCanvasElements } from '../../utils/off-canvas.util';
import { connect } from 'pwa-helpers';
import store from '../../../../store/store';
import { copyTextToClipboard } from '../../../../common/utils/util';
import { COMMON_OFF_CANVAS_IDS } from '../../interfaces/common-off-canvas.interface';
import {
  COPY_AND_SHARE_OFF_CANVAS_DEFAULT_LABEL,
  COPY_AND_SHARE_OFF_CANVAS_DEFAULT_TITLE,
  getShareOffCanvasConfig,
} from '../utils/common-off-canvas.util';

const KEY_PRESSES_TO_HANDLE = {
  ENTER: 'Enter',
  SPACE: ' ',
};

@customElement('mhecomm-copy-and-share-off-canvas')
export class CopyAndShareOffCanvas extends connect(store)(BaseComponent) {
  static styles = [
    css`
      uxt-icon[icon='clipboard'] svg.mh-icon {
        width: 2rem;
        height: 2rem;
      }

      #copy-content-to-share {
        padding: 0;
      }

      #copy-content-to-share:hover {
        background: rgb(var(--bs-light-rgb));
      }
    `,
  ];

  @property({ type: Object })
  offCanvasTitle: TemplateResult = html`${COPY_AND_SHARE_OFF_CANVAS_DEFAULT_TITLE}`;

  @property({ type: Object })
  label: TemplateResult = html`${COPY_AND_SHARE_OFF_CANVAS_DEFAULT_LABEL}`;

  @property({ type: String })
  data: string = '';

  @property({ type: String })
  successMessage: string = 'Link copied to clipboard.';

  @property({ type: Boolean })
  isCopyDisabled: boolean = false;

  @property({ type: Boolean })
  isNested: boolean = false;

  @property({ type: Function })
  onCopySuccess?: () => void;

  @state()
  showSuccessMessage: boolean = false;

  stateChanged(state: StoreState): void {
    super.stateChanged(state);
    this.handleOffCanvasActions(state);
  }

  renderSuccessMessage() {
    return html` <div
      id="success-message-container"
      class="fw-semibold text-success fs-small ${this.showSuccessMessage ? 'visible' : 'invisible'}"
      aria-live="assertive"
      role="alert"
    >
      ${this.showSuccessMessage ? this.successMessage : ''}
    </div>`;
  }

  private copyToClipboard() {
    copyTextToClipboard(this.data);
    this.showSuccessMessage = true;
    this.onCopySuccess?.();
  }

  private handleCopyClick(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    this.copyToClipboard();
  }

  private handleKeyPress(e: KeyboardEvent) {
    if (e.key === KEY_PRESSES_TO_HANDLE.ENTER || e.key === KEY_PRESSES_TO_HANDLE.SPACE) {
      e.preventDefault();
      e.stopPropagation();
      this.copyToClipboard();
    }
  }

  renderOffcanvasContent() {
    return html`
      <div>
        <label id="copy-field-label" for="copy-link-input">${this.label}</label>
      </div>
      <div class="d-flex flex-row align-items-center gap-2">
        <div class="flex-grow-1">
          <input readonly class="w-100" type="text" id="content-to-share-input" value="${this.data}" />
        </div>
        <button
          id="copy-content-to-share"
          class="btn btn-link"
          type="button"
          @click="${this.handleCopyClick}"
          @keydown="${this.handleKeyPress}"
          ?disabled="${this.isCopyDisabled}"
          aria-label="Copy to clipboard"
        >
          <uxt-icon icon="clipboard"></uxt-icon>
        </button>
      </div>
      ${this.renderSuccessMessage()}
    `;
  }

  renderComp() {
    return this.isNested
      ? this.renderOffcanvasContent()
      : html`
          <mhecomm-off-canvas
            .offCanvasConfigs="${{
              [COMMON_OFF_CANVAS_IDS.COPY_AND_SHARE_LINK]: getShareOffCanvasConfig(this.offCanvasTitle),
            }}"
          >
            <div slot=${COMMON_OFF_CANVAS_IDS.COPY_AND_SHARE_LINK + '-off-canvas-body'}>
              ${this.renderOffcanvasContent()}
            </div>
          </mhecomm-off-canvas>
        `;
  }

  /**
   * Handles off-canvas actions if the off-canvas ID is set.
   */
  private handleOffCanvasActions(state: StoreState): void {
    if (shouldClearOffCanvasElements(COMMON_OFF_CANVAS_IDS.COPY_AND_SHARE_LINK, state)) {
      this.showSuccessMessage = false;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-copy-and-share-off-canvas': CopyAndShareOffCanvas;
  }
}